import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { TForm } from '@/types/generated'
import { getDocument } from 'pdfjs-dist'
import setupPdfs, { CMAP_URL, CMAP_PACKED } from '@/utils/pdfjs'
import { useSignatures } from './signatureStore'

// Initialize PDF.js
setupPdfs()

// Types
export type PrefillData = {
  client_first_name?: string
  client_last_name?: string
  client_email?: string
  client_phone?: string
  probably_client_city?: string
  probably_client_zip?: string
  probably_client_street?: string
  probably_client_street_number?: string
  probably_client_date_of_birth?: string
  [key: string]: string | undefined
}

export type PdfAnnotation = {
  id: string
  name: string
  page: number
  type: number[] | string
  defaultValue: string
  value: string
  required: boolean
}

type PDFState = {
  form: TForm
  loadingTask?: any // PDFDocumentLoadingTask type from pdfjs
  isLoading: boolean
  error?: string
  pages: number
  currentPage: number
  annotationData?: PdfAnnotation[]
  signatures?: Record<string, { data_uri: string }>
  manuallyEdited?: boolean
  sentDate?: string
  hasLawfirmUnfilledRequiredFields?: boolean
  hasLawfirmMissingSignatures?: boolean
}

export const useNewPdfStore = defineStore('newPdf', () => {
  // State
  const selectedPdfs = ref<Map<string, PDFState>>(new Map())
  const prefillData = ref<PrefillData | null>(null)

  // Store to persist annotations across sessions
  const savedAnnotations = ref<Record<string, PdfAnnotation[]>>({})
  const signatureStore = useSignatures()

  // Getters
  const isLoading = computed(() => {
    return Array.from(selectedPdfs.value.values()).some((pdf) => pdf.isLoading)
  })

  // Actions
  async function setPrefillData(data: PrefillData | null) {
    prefillData.value = data
  }

  async function batchLoadPdfs(forms: TForm[]): Promise<Record<string, boolean>> {
    const results: Record<string, boolean> = {}

    // Load PDFs one at a time
    for (const form of forms) {
      try {
        await loadPdf(form)
        results[form.id] = true
      } catch (error) {
        console.error(`Error loading PDF ${form.id}:`, error)
        results[form.id] = false
      }
    }

    return results
  }

  async function loadPdf(form: TForm) {
    /**
     * Loads a PDF document and extracts its form fields and annotations.
     *
     * This function:
     * 1. Adds the PDF to the store with loading state
     * 2. Fetches the PDF from the server
     * 3. Extracts form fields and annotations
     * 4. Applies any saved annotations or prefill data
     * 5. Updates the store with the loaded PDF and its annotations
     *
     * If the PDF fails to load, it will be marked as failed in the store.
     *
     * @param form - The form object containing PDF metadata and file URL
     * @returns A promise that resolves when the PDF is loaded
     */

    const currentFormState = selectedPdfs.value.get(form.id) || {
      form,
      isLoading: true,
      pages: 1,
      currentPage: 1,
      manuallyEdited: false
    }
    selectedPdfs.value.set(form.id, currentFormState)

    try {
      // Create loading task using the configured worker with cMap support
      const loadingTask = getDocument({
        url: form.file as string,
        cMapUrl: CMAP_URL,
        cMapPacked: CMAP_PACKED
      })
      const pdf = selectedPdfs.value.get(form.id)
      if (pdf) {
        pdf.loadingTask = loadingTask
      }

      // Wait for PDF to load
      const pdfDoc = await loadingTask.promise
      const annotations: PdfAnnotation[] = []

      for (let i = 1; i <= pdfDoc.numPages; i++) {
        const page = await pdfDoc.getPage(i)
        const annots = await page.getAnnotations()
        // extract only form fields
        const fields = annots
          .filter((annot) => annot.subtype === 'Widget')
          .map((annot) => ({
            id: annot.id || '',
            name: annot.fieldName || '',
            page: page.pageNumber,
            type: annot.fieldType || '',
            defaultValue: annot.defaultAppearance || '',
            value: annot.value || '',
            required: annot.required || false
          }))
        annotations.push(...fields)
      }

      // Initialize annotation map - first check if we have saved annotations
      let annotationMap: PdfAnnotation[] = []

      // If we have saved annotations for this document, use them
      if (savedAnnotations.value[form.id]) {
        annotationMap = [...savedAnnotations.value[form.id]]
      } else {
        for (const annotation of annotations) {
          const newAnnotation = {
            id: annotation.id,
            name: annotation.name,
            page: annotation.page,
            type: annotation.type,
            defaultValue: annotation.defaultValue,
            value: annotation.value || '',
            required: annotation.required || false
          }

          // If we have prefill data, check if we should update the value
          if (prefillData.value) {
            const fieldName = annotation.name.toLowerCase()
            for (const [key, value] of Object.entries(prefillData.value)) {
              if (value && fieldName === key.toLowerCase()) {
                newAnnotation.value = value
                break
              }
            }
          }

          annotationMap.push(newAnnotation)
        }
        savedAnnotations.value[form.id] = [...annotationMap]
      }

      const pdfState = selectedPdfs.value.get(form.id)
      if (pdfState) {
        pdfState.pages = pdfDoc.numPages
        pdfState.isLoading = false
        pdfState.annotationData = annotationMap
        pdfState.hasLawfirmUnfilledRequiredFields = hasLawfirmUnfilledRequiredFields(form.id)
      }

      try {
        signatureStore.setPdfStore('new')
        const fieldObjects = await pdfDoc.getFieldObjects()
        if (fieldObjects) {
          await signatureStore.updateAll(fieldObjects as any)
          await signatureStore.findAllSignatureFields(fieldObjects as any)
        }
      } catch (error) {
        console.warn('Error handling signature fields:', error)
      }

      const pdfStateAfterSignatures = selectedPdfs.value.get(form.id)
      if (pdfStateAfterSignatures) {
        pdfStateAfterSignatures.hasLawfirmMissingSignatures = hasLawfirmMissingSignatures(form.id)
      }

      return pdfDoc
    } catch (error: any) {
      // Handle error
      const pdfState = selectedPdfs.value.get(form.id)
      if (pdfState) {
        pdfState.error = error.message
        pdfState.isLoading = false
      }
      throw error
    }
  }

  function updateSignatures(id: string, signatures: Record<string, { data_uri: string }>) {
    /**
     * Updates the signatures for a PDF document.
     *
     * @param id - The ID of the PDF document
     * @param signatures - Object containing signature field names and their data URIs
     */
    const pdfState = selectedPdfs.value.get(id)
    if (pdfState) {
      pdfState.signatures = signatures
    }
  }

  function updateAnnotation(id: string, fieldName: string, value: string) {
    /**
     * Updates the value of a PDF form field annotation.
     *
     * This function performs two updates:
     * 1. Updates the annotation in the current PDF state (in-memory)
     * 2. Persists the annotation in the savedAnnotations store
     * 3. Updates the missingRequiredFields status
     *
     * If the annotation doesn't exist in the savedAnnotations store,
     * it will be added with all its properties.
     *
     * @param id - The ID of the PDF document
     * @param fieldName - The name of the form field to update
     * @param value - The new value to set for the annotation
     */
    if (!id) return
    const pdf = selectedPdfs.value.get(id)
    if (pdf && pdf.annotationData) {
      const annotation = pdf.annotationData.find((a) => a.name === fieldName)

      if (annotation) {
        annotation.value = value

        if (!savedAnnotations.value[id]) {
          savedAnnotations.value[id] = []
        }

        const savedAnnot = savedAnnotations.value[id].find((a) => a.id === annotation.id)

        if (savedAnnot) {
          savedAnnot.value = value
        } else {
          savedAnnotations.value[id].push({
            id: annotation.id,
            name: annotation.name,
            page: annotation.page,
            type: annotation.type,
            defaultValue: annotation.defaultValue,
            value: value,
            required: annotation.required
          })
        }

        pdf.hasLawfirmUnfilledRequiredFields = hasLawfirmUnfilledRequiredFields(id)
        pdf.hasLawfirmMissingSignatures = hasLawfirmMissingSignatures(id)
      }
    }
  }

  function resetAnnotations(id: string) {
    /**
     * Resets the annotations of a PDF document. By clearing the
     * savedAnnotations store and re-loading the PDF, all manually added annotations
     * will be removed.
     *
     * @param id - The ID of the PDF document
     */
    const pdf = selectedPdfs.value.get(id)
    if (pdf) {
      delete savedAnnotations.value[id]
      loadPdf(pdf.form)
    }
  }

  function setCurrentPage(id: string, page: number) {
    const pdf = selectedPdfs.value.get(id)
    if (pdf) {
      // Only update if the page is different to prevent unnecessary updates
      if (pdf.currentPage !== page) {
        pdf.currentPage = page
      }
    }
  }

  function getManuallyEdited(id: string): boolean {
    const pdf = selectedPdfs.value.get(id)
    return pdf?.manuallyEdited || false
  }

  function setManuallyEdited(id: string, edited: boolean) {
    const pdf = selectedPdfs.value.get(id)
    if (pdf) {
      if (pdf.manuallyEdited !== edited) {
        pdf.manuallyEdited = edited
      }
    }
  }

  function getSentDate(id: string): string | null {
    const pdf = selectedPdfs.value.get(id)
    return pdf?.sentDate || null
  }

  function setSentDate(id: string, date: string) {
    const pdf = selectedPdfs.value.get(id)
    if (pdf) {
      if (pdf.sentDate !== date) {
        pdf.sentDate = date
      }
    }
  }

  function getAnnotations(id: string): PdfAnnotation[] {
    return savedAnnotations.value[id] || []
  }

  function getAnnotationsMap(id: string): Record<string, { value: string }> {
    if (!id) return {}
    const fileAnnotations = savedAnnotations.value[id] || []
    const pdfAnnotations = fileAnnotations.reduce(
      (annotsMap, annotation) => {
        annotsMap[annotation.id] = { value: annotation.value }
        return annotsMap
      },
      {} as Record<string, { value: string }>
    )

    let signatureAnnotations = {}
    if (signatureStore) {
      signatureAnnotations = Object.fromEntries(
        Object.entries(signatureStore.signatures || {}).map(([key, signature]: [string, any]) => [
          key,
          { value: signature.data_uri }
        ])
      )
    }

    return {
      ...pdfAnnotations,
      ...signatureAnnotations
    }
  }

  function hasLawfirmUnfilledRequiredFields(id: string): boolean {
    if (!id) return false

    const pdf = selectedPdfs.value.get(id)
    if (!pdf || !pdf.annotationData) return false

    return pdf.annotationData.some((annotation) => {
      if (!annotation.name.endsWith('_lawfirm')) return false
      if (!annotation.required) return false
      if (annotation.name.includes('signature')) return false

      const value = annotation.value?.toString().trim() || ''
      return value === ''
    })
  }

  function hasLawfirmMissingSignatures(id: string): boolean {
    if (!id) return false

    const pdf = selectedPdfs.value.get(id)
    if (!pdf || !pdf.annotationData) return false

    const lawfirmSignatureFields = pdf.annotationData
      .filter((annotation) => {
        return annotation.name.endsWith('_lawfirm') && annotation.name.includes('signature')
      })
      .map((annotation) => annotation.name)

    if (lawfirmSignatureFields.length === 0) return false

    return lawfirmSignatureFields.some((fieldName) => {
      const signatureKey = id + fieldName
      const signature = signatureStore.signatures[signatureKey]
      return !signature || !signature.data_uri || signature.data_uri.trim() === ''
    })
  }

  function getAllUnfilledRequiredFields(): Record<string, boolean> {
    const result: Record<string, boolean> = {}

    for (const [id] of selectedPdfs.value) {
      result[id] = hasLawfirmUnfilledRequiredFields(id)
    }

    return result
  }

  function removePdf(id: string) {
    const pdf = selectedPdfs.value.get(id)
    if (pdf?.loadingTask) {
      try {
        safelyDestroyPdfTask(pdf.loadingTask)
      } finally {
        // Always null out the reference
        pdf.loadingTask = null
      }
    }
    // Always remove from the map
    selectedPdfs.value.delete(id)

    if (signatureStore) {
      if (selectedPdfs.value.size === 0) {
        signatureStore.clearSignatures()
      } else {
        const signaturesKeys = Object.keys(signatureStore.signatures).filter((key) =>
          key.startsWith(id)
        )
        signaturesKeys.forEach((key) => delete signatureStore.signatures[key])
      }
    }
  }

  function clear() {
    const idsToKeep = new Set<string>()
    // Don't destroy loading tasks that are still loading
    for (const [id, pdf] of selectedPdfs.value.entries()) {
      if (pdf.isLoading) {
        idsToKeep.add(id)
      }
    }
    // Now destroy only the tasks that are not in the keep list
    for (const [id, pdf] of selectedPdfs.value.entries()) {
      if (!idsToKeep.has(id) && pdf.loadingTask) {
        try {
          safelyDestroyPdfTask(pdf.loadingTask)
        } finally {
          // Always null out the reference
          pdf.loadingTask = null
        }
      }
    }
    // Remove all PDFs except those that are still loading
    for (const [id, pdf] of selectedPdfs.value.entries()) {
      if (!idsToKeep.has(id)) {
        selectedPdfs.value.delete(id)
      }
    }

    if (signatureStore) {
      signatureStore.clearSignatures()
    }
  }

  function safelyDestroyPdfTask(loadingTask: any) {
    if (!loadingTask) return
    try {
      if (loadingTask.promise) {
        loadingTask.promise.catch(() => {})
      }
      if (typeof loadingTask.destroy === 'function') {
        try {
          loadingTask.destroy()
        } catch (e) {
          console.warn('Error destroying PDF task:', e)
        }
      }
    } catch (error) {
      console.error('Error cleaning up PDF resources:', error)
    }
  }

  function onPdfLoaded(id: string, pages: number) {
    const pdf = selectedPdfs.value.get(id)
    if (pdf) {
      pdf.pages = pages
      pdf.isLoading = false
    }
  }

  return {
    // State
    selectedPdfs,
    prefillData,
    savedAnnotations,
    signatureStore,

    // Getters
    isLoading,

    // Actions
    setPrefillData,
    loadPdf,
    batchLoadPdfs,
    setCurrentPage,
    getManuallyEdited,
    setManuallyEdited,
    getSentDate,
    setSentDate,
    getAnnotationsMap,
    updateAnnotation,
    updateSignatures,
    resetAnnotations,
    getAnnotations,
    hasLawfirmUnfilledRequiredFields,
    getAllUnfilledRequiredFields,
    hasLawfirmMissingSignatures,
    removePdf,
    clear,
    onPdfLoaded
  }
})
